// src/pages/ProductDetail.js
import React, { useEffect, useState } from 'react';
import { Container, Typography, Box, Chip, Divider, CircularProgress } from '@mui/material';
import { useParams } from 'react-router-dom';
import axiosInstance from '../services/api';
import { getFullImageUrl } from '../utils/urlHelpers';
import ProductGallery from '../components/ProductGallery';
import CompatibilityTable from '../components/CompatibilityTable';
import SeoHead from '../components/SeoHead';

const ProductDetail = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        // Önce slug ile dene
        const res = await axiosInstance.get(`/products/slug/${slug}`);
        setProduct(res.data);
      } catch {
        // Eski format: isim-id şeklinde
        const parts = slug.split('-');
        const lastPart = parts[parts.length - 1];
        const numId = parseInt(lastPart, 10);
        if (!isNaN(numId)) {
          try {
            const res2 = await axiosInstance.get(`/products/${numId}`);
            setProduct(res2.data);
          } catch {
            setNotFound(true);
          }
        } else {
          setNotFound(true);
        }
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [slug]);

  if (loading) {
    return <Container sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Container>;
  }

  if (notFound || !product) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5">Ürün bulunamadı.</Typography>
      </Container>
    );
  }

  // Görseller: images dizisi varsa kullan, yoksa imageUrl'den oluştur
  const images = product.images && product.images.length > 0
    ? product.images
    : (product.imageUrl ? [{ imageUrl: product.imageUrl, isMain: true }] : []);

  const mainImage = images.find(i => i.isMain) || images[0];
  const ogImage = mainImage ? getFullImageUrl(mainImage.imageUrl) : null;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: ogImage,
    sku: product.oemCode || undefined,
    brand: { '@type': 'Brand', name: 'İrem Oto' },
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/InStock',
      priceCurrency: 'TRY',
      seller: { '@type': 'Organization', name: 'İrem Oto' }
    }
  };

  return (
    <>
      <SeoHead
        title={product.metaTitle || product.name}
        description={product.metaDescription || product.description?.substring(0, 160)}
        keywords={product.metaKeywords}
        image={ogImage}
        jsonLd={jsonLd}
      />
      <Container sx={{ py: 6 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 5 }}>
          {/* Sol: Galeri */}
          <Box sx={{ flex: 1 }}>
            <ProductGallery images={images} productName={product.name} />
          </Box>

          {/* Sağ: Bilgiler */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              {product.name}
            </Typography>

            {product.oemCode && (
              <Box sx={{ mb: 2 }}>
                <Chip label={`OEM: ${product.oemCode}`} variant="outlined" color="primary" />
              </Box>
            )}

            {product.productSubTypeDto && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Kategori: <strong>{product.productSubTypeDto.name}</strong>
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="body1" sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>
              {product.description}
            </Typography>

            <Box sx={{ mt: 3 }}>
              <Chip label="Stoğumuzda Mevcut" color="success" />
            </Box>
          </Box>
        </Box>

        {/* Araç Uyumluluk Tablosu */}
        <CompatibilityTable compatibilities={product.compatibilities || []} />
      </Container>
    </>
  );
};

export default ProductDetail;
